import { useEffect, useRef } from 'react';
import { useStore } from '@contexts/StoreContext';
import { useTheme } from '@contexts/ThemeContext';
import { useOrgConfigOptional } from '@contexts/OrgConfigContext';
import {
  getThemeByBrand,
  generateDynamicTheme,
  orgBrandingToClientTheme,
} from '@utils/operatorThemes';

/**
 * Applies branding to the active ClientTheme.
 * Priority:
 *   1. OrgConfig from cms (full palette + logo + favicon) — when provider present.
 *   2. Store brandConfig (legacy single colour path).
 *   3. Hardcoded theme by brandName.
 * Respects manual theme selection.
 */
export const useBrandTheme = (): void => {
  const { data: storeData, basicData } = useStore();
  const { setClientTheme, isThemeManuallySelected } = useTheme();
  const orgCfg = useOrgConfigOptional();
  const branding = orgCfg?.branding ?? null;
  const appliedBrandNameRef = useRef<string | null>(null);

  // Early theme application from basicData.brandName (loads before OrgConfig)
  useEffect(() => {
    if (isThemeManuallySelected) return;
    if (branding) return;
    if (storeData?.brandConfig?.primaryColor) return;

    const brandName = basicData?.brandName;
    if (!brandName || brandName === appliedBrandNameRef.current) return;

    const brandTheme = getThemeByBrand(brandName);
    if (brandTheme) {
      appliedBrandNameRef.current = brandName;
      setClientTheme(brandTheme, false);
    }
  }, [
    basicData?.brandName,
    storeData?.brandConfig,
    branding,
    setClientTheme,
    isThemeManuallySelected,
  ]);

  // Full theme application
  useEffect(() => {
    if (isThemeManuallySelected) return;

    // Priority 1: org config from CMS
    if (branding) {
      setClientTheme(orgBrandingToClientTheme(branding), false);
      return;
    }

    // Priority 2: legacy store brandConfig (single colour)
    const brandConfig = storeData?.brandConfig;
    if (brandConfig?.primaryColor) {
      setClientTheme(generateDynamicTheme(brandConfig), false);
      return;
    }

    // Priority 3: hardcoded by brand name
    const brand = storeData?.brand || basicData?.brandName || 'qikserve';
    const brandTheme = getThemeByBrand(brand);
    if (brandTheme) {
      setClientTheme(brandTheme, false);
    }
  }, [
    branding,
    storeData?.brand,
    storeData?.brandConfig,
    basicData?.brandName,
    setClientTheme,
    isThemeManuallySelected,
  ]);
};

export default useBrandTheme;
